<template>
  <div class="fixed bottom-6 right-6 z-50">
    <!-- Bouton flottant -->
    <button
      @click="togglePanel"
      class="relative w-14 h-14 bg-teal rounded-full shadow-lg hover:bg-teal-hover transition-all duration-300 flex items-center justify-center group hover:scale-110"
      :class="{ 'ring-4 ring-teal/30': isOpen }"
    >
      <i class="pi pi-bell text-offwhite text-xl"></i>
      <!-- Badge de compteur -->
      <span
        v-if="notificationsStore.unreadCount > 0"
        class="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse"
      >
        {{ notificationsStore.unreadCount > 99 ? '99+' : notificationsStore.unreadCount }}
      </span>
      <!-- Indicateur de connexion WebSocket -->
      <span
        class="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white"
        :class="{
          'bg-green-500': notificationsStore.connected,
          'bg-gray-400': !notificationsStore.connected && !notificationsStore.reconnecting,
          'bg-yellow-500 animate-pulse': notificationsStore.reconnecting
        }"
        :title="notificationsStore.connected ? 'Connecté' : notificationsStore.reconnecting ? 'Reconnexion...' : 'Déconnecté'"
      ></span>
    </button>

    <!-- Panel de notifications (style chatbox) -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform translate-y-4 opacity-0"
      enter-to-class="transform translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="transform translate-y-0 opacity-100"
      leave-to-class="transform translate-y-4 opacity-0"
    >
      <div
        v-if="isOpen"
        class="absolute bottom-20 right-0 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
      >
        <!-- Header -->
        <div class="bg-gradient-to-r from-teal to-teal-hover px-5 py-4 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <i class="pi pi-bell text-offwhite text-lg"></i>
            <div>
              <h3 class="text-offwhite font-semibold text-base">Notifications</h3>
              <p class="text-offwhite/70 text-xs">
                {{ notificationsStore.unreadCount }} non lue(s)
                <span v-if="!notificationsStore.connected" class="ml-2">
                  • {{ notificationsStore.reconnecting ? 'Reconnexion...' : 'Déconnecté' }}
                </span>
              </p>
            </div>
          </div>
          <button
            @click="markAllAsRead"
            v-if="notificationsStore.unreadCount > 0"
            class="text-offwhite/80 hover:text-offwhite text-xs underline"
          >
            Tout marquer lu
          </button>
        </div>

        <!-- Liste des notifications -->
        <div class="max-h-[500px] overflow-y-auto">
          <div v-if="loading" class="p-8 text-center">
            <i class="pi pi-spin pi-spinner text-teal text-3xl"></i>
            <p class="text-navy/50 text-sm mt-2">Chargement...</p>
          </div>

          <div v-else-if="notificationsStore.notifications.length === 0" class="p-8 text-center">
            <i class="pi pi-bell-slash text-navy/20 text-5xl mb-3"></i>
            <p class="text-navy/50 text-sm">Aucune notification</p>
          </div>

          <div v-else class="divide-y divide-gray-100">
            <div
              v-for="notif in notificationsStore.recentNotifications"
              :key="notif.id"
              @click="handleNotificationClick(notif)"
              class="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              :class="{ 'bg-teal/5': !notif.read }"
            >
              <div class="flex gap-3">
                <!-- Icône selon le type -->
                <div
                  class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  :class="getNotificationIconClass(notif.event)"
                >
                  <i :class="getNotificationIcon(notif.event)" class="text-base"></i>
                </div>

                <!-- Contenu -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-start justify-between gap-2 mb-1">
                    <p class="text-navy font-medium text-sm truncate">
                      {{ notif.title }}
                    </p>
                    <span
                      v-if="!notif.read"
                      class="w-2 h-2 bg-teal rounded-full flex-shrink-0 mt-1"
                    ></span>
                  </div>
                  <p class="text-navy/60 text-xs line-clamp-2 mb-2">
                    {{ notif.message }}
                  </p>
                  <div class="flex items-center justify-between">
                    <span class="text-navy/40 text-xs">
                      {{ formatDate(notif.createdAt) }}
                    </span>
                    <button
                      v-if="!notif.read"
                      @click.stop="markAsRead(notif.id)"
                      class="text-teal text-xs hover:underline"
                    >
                      Marquer lu
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="border-t border-gray-200 p-3 bg-gray-50">
          <router-link
            to="/notifications"
            @click="isOpen = false"
            class="block text-center text-teal text-sm font-medium hover:underline"
          >
            Voir toutes les notifications
          </router-link>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from '@/composables/useToast'
import { useNotificationsStore } from '@/stores/notifications'
import notificationService from '@/services/notificationService'

const router = useRouter()
const toast = useToast()
const notificationsStore = useNotificationsStore()

const isOpen = ref(false)
const loading = ref(false)

// Toggle du panel
const togglePanel = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value && notificationsStore.notifications.length === 0) {
    loadNotifications()
  }
}

// Charger les notifications via API (fallback si WebSocket n'est pas connecté)
const loadNotifications = async () => {
  loading.value = true
  try {
    const response = await notificationService.getAllNotifications({ limit: 20 })
    const data = response.data || response || []
    
    // Si WebSocket n'est pas connecté, on utilise les données de l'API
    if (!notificationsStore.connected && Array.isArray(data)) {
      // Vider les notifications existantes pour éviter les doublons
      notificationsStore.notifications = []
      notificationsStore.unreadCount = 0
      
      // Mapper les données pour le format du store
      data.forEach(n => {
        notificationsStore.addNotification(n.type || 'SYSTEM', {
          id: n.id,
          title: n.title || undefined, // Laisser undefined pour que le store génère le titre
          message: n.message || '',
          createdAt: n.createdAt || new Date().toISOString(),
          read: !!n.readAt,
          metadata: n.metadata || {},
          type: n.type
        })
      })
    }
  } catch (error) {
    console.error('Erreur chargement notifications:', error)
  } finally {
    loading.value = false
  }
}

// Marquer comme lu
const markAsRead = async (notifId) => {
  try {
    await notificationService.markAsRead(notifId)
    notificationsStore.markAsRead(notifId)
  } catch (error) {
    console.error('Erreur marquage notification:', error)
    toast.error('Erreur lors du marquage de la notification')
  }
}

// Marquer toutes comme lues
const markAllAsRead = async () => {
  try {
    await notificationService.markAllAsRead()
    notificationsStore.markAllAsRead()
    toast.success('Toutes les notifications ont été marquées comme lues')
  } catch (error) {
    console.error('Erreur marquage notifications:', error)
    toast.error('Erreur lors du marquage des notifications')
  }
}

// Gérer le clic sur une notification
const handleNotificationClick = (notif) => {
  markAsRead(notif.id)
  
  // Rediriger selon le type/event de notification
  const event = notif.event || notif.type
  
  switch (event) {
    case 'STOCK_ALERT':
    case 'stock:low':
    case 'stock:critical':
    case 'stock:out':
      router.push('/achat/alertes')
      break
    case 'ORDER':
    case 'order:created':
    case 'order:approved':
    case 'order:received':
      router.push('/achat/commandes')
      break
    case 'APPROVAL':
    case 'transfer:requested':
      router.push('/achat/demandes')
      break
    case 'DISPATCH':
    case 'dispatch:created':
      router.push('/achat/dispatch')
      break
    case 'RECEPTION':
    case 'reception:created':
      router.push('/achat/receptions')
      break
    default:
      // Si la notification a un lien spécifique
      if (notif.link || notif.data?.link) {
        router.push(notif.link || notif.data.link)
      }
  }
  
  isOpen.value = false
}

// Icône selon le type/event
const getNotificationIcon = (event) => {
  const icons = {
    'STOCK_ALERT': 'pi pi-exclamation-triangle',
    'stock:low': 'pi pi-exclamation-triangle',
    'stock:critical': 'pi pi-exclamation-triangle',
    'stock:out': 'pi pi-times-circle',
    'ORDER': 'pi pi-shopping-cart',
    'order:created': 'pi pi-shopping-cart',
    'order:approved': 'pi pi-check-circle',
    'order:received': 'pi pi-inbox',
    'APPROVAL': 'pi pi-check-circle',
    'transfer:requested': 'pi pi-arrow-right',
    'transfer:approved': 'pi pi-check',
    'DISPATCH': 'pi pi-send',
    'dispatch:created': 'pi pi-send',
    'RECEPTION': 'pi pi-inbox',
    'reception:created': 'pi pi-inbox',
    'SYSTEM': 'pi pi-info-circle',
    'LOW_STOCK': 'pi pi-exclamation-triangle',
    'OUT_OF_STOCK': 'pi pi-times-circle'
  }
  return icons[event] || 'pi pi-bell'
}

// Classe CSS selon le type/event
const getNotificationIconClass = (event) => {
  const classes = {
    'STOCK_ALERT': 'bg-red-100 text-red-600',
    'stock:low': 'bg-orange-100 text-orange-600',
    'stock:critical': 'bg-red-100 text-red-600',
    'stock:out': 'bg-red-100 text-red-600',
    'ORDER': 'bg-blue-100 text-blue-600',
    'order:created': 'bg-blue-100 text-blue-600',
    'order:approved': 'bg-green-100 text-green-600',
    'order:received': 'bg-yellow-100 text-yellow-600',
    'APPROVAL': 'bg-green-100 text-green-600',
    'transfer:requested': 'bg-purple-100 text-purple-600',
    'transfer:approved': 'bg-green-100 text-green-600',
    'DISPATCH': 'bg-purple-100 text-purple-600',
    'dispatch:created': 'bg-purple-100 text-purple-600',
    'RECEPTION': 'bg-yellow-100 text-yellow-600',
    'reception:created': 'bg-yellow-100 text-yellow-600',
    'SYSTEM': 'bg-gray-100 text-gray-600',
    'LOW_STOCK': 'bg-orange-100 text-orange-600',
    'OUT_OF_STOCK': 'bg-red-100 text-red-600'
  }
  return classes[event] || 'bg-teal/10 text-teal'
}

// Formater la date
const formatDate = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diff = now - date
  
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  
  if (minutes < 1) return 'À l\'instant'
  if (minutes < 60) return `Il y a ${minutes} min`
  if (hours < 24) return `Il y a ${hours}h`
  if (days < 7) return `Il y a ${days}j`
  
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}

// Fermer au clic extérieur
const handleClickOutside = (event) => {
  const target = event.target
  if (isOpen.value && !target.closest('.fixed.bottom-6.right-6')) {
    isOpen.value = false
  }
}

// Lifecycle
onMounted(() => {
  // Connecter le WebSocket
  notificationsStore.connect()
  
  // Charger les notifications initiales
  loadNotifications()
  
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Animation du badge */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Scrollbar personnalisée */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #2DD4BF;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #0B8A74;
}

/* Line clamp */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
